import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Mail, Check, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import {
  getMeetingRoomById,
  updateMeetingRoom,
  checkPermalinkExists,
} from "../../../../services/roomServices";
import userService from "../../../../services/userServices";
import { useSiteContext } from "../../../../context/SiteContext";

const generateRandomString = (length = 10) => {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const EditRoomModal = ({ isOpen, onClose, room, onUpdate, onDelete }) => {
  const { user } = useSiteContext();
  const isBasicPlan = user?.plan !== "Business";
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [meetingName, setMeetingName] = useState("");
  const [vanityName, setVanityName] = useState("");
  const [originalPermalink, setOriginalPermalink] = useState("");
  const [permalinkChecking, setPermalinkChecking] = useState(false);
  const [permalinkExists, setPermalinkExists] = useState(false);
  const [permalinkMessage, setPermalinkMessage] = useState("");
  const [roomNameFromApi, setRoomNameFromApi] = useState(false);
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});

  const [roomSettings, setRoomSettings] = useState({
    allowChat: true,
    allowScreenSharing: false,
    muteParticipantsOnEntry: false,
    allowReactions: true,
    allowRaiseHand: true,
    requirePassword: false,
    password: "",
    waitingRoom: false,
    privacy: false,
  });

  // Co-Host state
  const [invitesEmailInput, setInvitesEmailInput] = useState("");
  const [invitesList, setInvitesList] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const [checkingUser, setCheckingUser] = useState(false);
  const [userExists, setUserExists] = useState(null);
  const [inviteEmails, setInviteEmails] = useState([]);

  // Speakers state
  const [speakersEmailInput, setSpeakersEmailInput] = useState("");
  const [speakersList, setSpeakersList] = useState([]);
  const [showSpeakerSuggestions, setShowSpeakerSuggestions] = useState(false);
  const [selectedSpeakerSuggestionIndex, setSelectedSpeakerSuggestionIndex] = useState(-1);
  const [speakerSuggestions, setSpeakerSuggestions] = useState([]);
  const [checkingSpeakerUser, setCheckingSpeakerUser] = useState(false);
  const [speakerUserExists, setSpeakerUserExists] = useState(null);
  const [speakerInviteEmails, setSpeakerInviteEmails] = useState([]);

  const [exitUrlEnabled, setExitUrlEnabled] = useState(false);
  const [exitUrl, setExitUrl] = useState("");

  const [customButtonEnabled, setCustomButtonEnabled] = useState(false);
  const [customButtonUrl, setCustomButtonUrl] = useState("");
  const [customButtonText, setCustomButtonText] = useState("");

  const [waitingRoomTextEnabled, setWaitingRoomTextEnabled] = useState(false);
  const [waitingRoomTitle, setWaitingRoomTitle] = useState("");
  const [waitingRoomMessage, setWaitingRoomMessage] = useState("");
  const [waitingRoomSubText, setWaitingRoomSubText] = useState("");

  const [customBackgroundEnabled, setCustomBackgroundEnabled] = useState(false);
  const [backgroundType, setBackgroundType] = useState("solid");
  const [backgroundColor, setBackgroundColor] = useState("#1a1a2e");
  const [gradientColor1, setGradientColor1] = useState("#1a1a2e");
  const [gradientColor2, setGradientColor2] = useState("#615fff");
  const [gradientDirection, setGradientDirection] = useState("to right");
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState(null);

  // Sub-modal state
  const [activeSubModal, setActiveSubModal] = useState(null); // 'exitUrl' | 'customButton' | 'waitingRoomText' | null

  const commonDomains = [
    "gmail.com",
    "yahoo.com",
    "outlook.com",
    "hotmail.com",
    "icloud.com",
  ];

  const isValidUrl = (urlString) => {
    try {
      new URL(urlString);
      return true;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    if (isOpen && room?.id) {
      loadRoomData();
    }
  }, [isOpen, room]);

  const loadRoomData = async () => {
    setLoading(true);
    try {
      const response = await getMeetingRoomById(room.id);
      const roomData = response.data;

      if (roomData.roomName) {
        setMeetingName(roomData.roomName);
        setRoomNameFromApi(true);
      } else {
        setMeetingName("");
      }
      // Vanity Name (Room URL) is independent from Room Name
      // Treat "false", "true", empty, or missing roomPermalink as unset
      const permalink = roomData.roomPermalink && roomData.roomPermalink !== "false" && roomData.roomPermalink !== "true"
        ? roomData.roomPermalink
        : "";
      setOriginalPermalink(permalink);
      setPermalinkExists(false);
      setPermalinkMessage("");
      if (isBasicPlan) {
        setVanityName(permalink || generateRandomString());
      } else {
        setVanityName(permalink);
      }
      setDescription(roomData.description || "");

      setRoomSettings({
        allowChat: roomData.allowChat ?? true,
        allowScreenSharing: roomData.allowScreenSharing ?? false,
        muteParticipantsOnEntry: roomData.muteParticipants ?? false,
        allowReactions: roomData.allowReactions ?? true,
        allowRaiseHand: roomData.allowRaiseHand ?? true,
        requirePassword: roomData.requirePassword ?? false,
        password: roomData.password || "",
        waitingRoom: roomData.waitingRoom ?? false,
        privacy: roomData.privacy ?? false,
      });

      // Load co-hosts (backend returns resolved emails in coHostEmails)
      let coHostData = roomData.coHostEmails || roomData.coHosts || roomData.coHost || [];
      if (typeof coHostData === "string" && coHostData.trim()) {
        try {
          coHostData = JSON.parse(coHostData);
        } catch (e) {
          coHostData = [];
        }
      }
      setInvitesList(Array.isArray(coHostData) ? coHostData : []);

      // Load co-host invite emails (non-registered users) for orange badge display
      let coHostInviteData = roomData.coHostInviteEmails || roomData.coHostInvites || [];
      if (typeof coHostInviteData === "string" && coHostInviteData.trim()) {
        try {
          coHostInviteData = JSON.parse(coHostInviteData);
        } catch (e) {
          coHostInviteData = [];
        }
      }
      setInviteEmails(Array.isArray(coHostInviteData) ? coHostInviteData : []);

      // Load speakers
      let speakersData = roomData.speakers || [];
      if (typeof speakersData === "string" && speakersData.trim()) {
        try {
          speakersData = JSON.parse(speakersData);
        } catch (e) {
          speakersData = [];
        }
      }
      setSpeakersList(Array.isArray(speakersData) ? speakersData : []);

      // Load speaker invite emails (non-registered users) for orange badge display
      let speakerInviteData = roomData.speakerInviteEmails || roomData.speakersInvites || roomData.presentersInvites || [];
      if (typeof speakerInviteData === "string" && speakerInviteData.trim()) {
        try {
          speakerInviteData = JSON.parse(speakerInviteData);
        } catch (e) {
          speakerInviteData = [];
        }
      }
      setSpeakerInviteEmails(Array.isArray(speakerInviteData) ? speakerInviteData : []);

      // Exit URL
      const exitUrlValue = roomData.exitUrl;
      const hasExitUrl = exitUrlValue && exitUrlValue !== "false" && exitUrlValue !== false && exitUrlValue.trim() !== "";
      setExitUrlEnabled(roomData.exitUrlEnabled ?? hasExitUrl);
      setExitUrl(hasExitUrl ? exitUrlValue : "");

      // Custom Button
      const hasCustomButton = roomData.customButtonText?.trim() && roomData.customButtonUrl?.trim();
      setCustomButtonEnabled(roomData.customButtonEnabled ?? hasCustomButton);
      setCustomButtonText(roomData.customButtonText || "");
      setCustomButtonUrl(roomData.customButtonUrl || "");

      // Waiting Room Text
      const hasWaitingRoomText = roomData.waitingRoomTitle?.trim() || roomData.waitingRoomMessage?.trim() || roomData.waitingRoomSubText?.trim();
      setWaitingRoomTextEnabled(roomData.waitingRoomTextEnabled ?? hasWaitingRoomText);
      setWaitingRoomTitle(roomData.waitingRoomTitle || "");
      setWaitingRoomMessage(roomData.waitingRoomMessage || "");
      setWaitingRoomSubText(roomData.waitingRoomSubText || "");

      // Custom Background
      const backgroundImageUrl = roomData.meetingBackgroundImage;
      const hasBackgroundImage = backgroundImageUrl && backgroundImageUrl.trim() !== "";
      const solidColor = roomData.meetingBackgroundSolid;
      const hasCustomColor = solidColor && solidColor !== "" && solidColor !== "#1a1a2e";

      // Parse gradient JSON from DB
      let parsedGradient = {};
      if (roomData.meetingBackgroundGradient) {
        try {
          parsedGradient = JSON.parse(roomData.meetingBackgroundGradient);
        } catch (e) {
          parsedGradient = {};
        }
      }
      const hasCustomGradient = parsedGradient.gradientColor1 || parsedGradient.gradientColor2;

      const hasCustomBackground = hasBackgroundImage || hasCustomColor || hasCustomGradient;
      setCustomBackgroundEnabled(roomData.customBackgroundEnabled ?? hasCustomBackground);

      let detectedBackgroundType = "solid";
      if (hasBackgroundImage) {
        detectedBackgroundType = "image";
      } else if (hasCustomGradient) {
        detectedBackgroundType = "gradient";
      }

      setBackgroundType(roomData.meetingBackgroundType || detectedBackgroundType);
      setBackgroundColor(solidColor || "#1a1a2e");
      setGradientColor1(parsedGradient.gradientColor1 || "#1a1a2e");
      setGradientColor2(parsedGradient.gradientColor2 || "#615fff");
      setGradientDirection(parsedGradient.gradientDirection || "to right");

      if (hasBackgroundImage) {
        setBackgroundImagePreview(backgroundImageUrl);
      }
    } catch (error) {
      console.error("Error loading room data:", error);
      toast.error("Failed to load room data");
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (setting, value) => {
    setRoomSettings((prev) => ({
      ...prev,
      [setting]: value,
    }));
  };

  // Email suggestions for co-hosts
  useEffect(() => {
    const input = invitesEmailInput.trim().toLowerCase();
    if (input.length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const newSuggestions = [];
    const hasAtSymbol = input.includes("@");
    const parts = input.split("@");

    if (hasAtSymbol && parts.length === 2) {
      const [localPart, domainPart] = parts;
      if (domainPart.length > 0) {
        commonDomains.forEach((domain) => {
          if (domain.startsWith(domainPart) && domain !== domainPart) {
            const suggestion = `${localPart}@${domain}`;
            if (!invitesList.includes(suggestion)) {
              newSuggestions.push(suggestion);
            }
          }
        });
      } else {
        commonDomains.forEach((domain) => {
          const suggestion = `${localPart}@${domain}`;
          if (!invitesList.includes(suggestion)) {
            newSuggestions.push(suggestion);
          }
        });
      }
    } else if (!hasAtSymbol) {
      commonDomains.forEach((domain) => {
        const suggestion = `${input}@${domain}`;
        if (!invitesList.includes(suggestion)) {
          newSuggestions.push(suggestion);
        }
      });
    }

    setSuggestions(newSuggestions.slice(0, 5));
    setShowSuggestions(newSuggestions.length > 0);
    setSelectedSuggestionIndex(-1);
  }, [invitesEmailInput, invitesList]);

  // Check if co-host user exists
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const input = invitesEmailInput.trim();

    if (input.length === 0) {
      setUserExists(null);
      setCheckingUser(false);
      return;
    }

    if (emailRegex.test(input)) {
      const timeoutId = setTimeout(async () => {
        setCheckingUser(true);
        try {
          const response = await userService.checkUserExists(input);
          const exists = response?.data?.exists ?? response?.exists ?? false;
          setUserExists(exists);
        } catch (error) {
          setUserExists(false);
        } finally {
          setCheckingUser(false);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setUserExists(null);
    }
  }, [invitesEmailInput]);

  // Email suggestions for speakers
  useEffect(() => {
    const input = speakersEmailInput.trim().toLowerCase();
    if (input.length === 0) {
      setSpeakerSuggestions([]);
      setShowSpeakerSuggestions(false);
      return;
    }

    const newSuggestions = [];
    const hasAtSymbol = input.includes("@");
    const parts = input.split("@");

    if (hasAtSymbol && parts.length === 2) {
      const [localPart, domainPart] = parts;
      if (domainPart.length > 0) {
        commonDomains.forEach((domain) => {
          if (domain.startsWith(domainPart) && domain !== domainPart) {
            const suggestion = `${localPart}@${domain}`;
            if (!speakersList.includes(suggestion)) {
              newSuggestions.push(suggestion);
            }
          }
        });
      } else {
        commonDomains.forEach((domain) => {
          const suggestion = `${localPart}@${domain}`;
          if (!speakersList.includes(suggestion)) {
            newSuggestions.push(suggestion);
          }
        });
      }
    } else if (!hasAtSymbol) {
      commonDomains.forEach((domain) => {
        const suggestion = `${input}@${domain}`;
        if (!speakersList.includes(suggestion)) {
          newSuggestions.push(suggestion);
        }
      });
    }

    setSpeakerSuggestions(newSuggestions.slice(0, 5));
    setShowSpeakerSuggestions(newSuggestions.length > 0);
    setSelectedSpeakerSuggestionIndex(-1);
  }, [speakersEmailInput, speakersList]);

  // Check if speaker user exists
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const input = speakersEmailInput.trim();

    if (input.length === 0) {
      setSpeakerUserExists(null);
      setCheckingSpeakerUser(false);
      return;
    }

    if (emailRegex.test(input)) {
      const timeoutId = setTimeout(async () => {
        setCheckingSpeakerUser(true);
        try {
          const response = await userService.checkUserExists(input);
          const exists = response?.data?.exists ?? response?.exists ?? false;
          setSpeakerUserExists(exists);
        } catch (error) {
          setSpeakerUserExists(false);
        } finally {
          setCheckingSpeakerUser(false);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setSpeakerUserExists(null);
    }
  }, [speakersEmailInput]);

  const handleAddCoHost = async (email, isInvite = null) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    if (isInvite === null) {
      try {
        setCheckingUser(true);
        const response = await userService.checkUserExists(trimmedEmail);
        const exists = response?.data?.exists ?? response?.exists ?? false;
        isInvite = !exists;
      } catch (error) {
        isInvite = true;
      } finally {
        setCheckingUser(false);
      }
    }

    if (!invitesList.includes(trimmedEmail)) {
      setInvitesList((prev) => [...prev, trimmedEmail]);
      if (isInvite) {
        setInviteEmails((prev) => [...prev, trimmedEmail]);
      }
      setInvitesEmailInput("");
      setShowSuggestions(false);
      setUserExists(null);
      toast.success(isInvite ? "Email invite added" : "Co-host added");
    } else {
      toast.warning("Email already added.");
    }
  };

  const handleAddSpeaker = async (email, isInvite = null) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    if (isInvite === null) {
      try {
        setCheckingSpeakerUser(true);
        const response = await userService.checkUserExists(trimmedEmail);
        const exists = response?.data?.exists ?? response?.exists ?? false;
        isInvite = !exists;
      } catch (error) {
        isInvite = true;
      } finally {
        setCheckingSpeakerUser(false);
      }
    }

    if (!speakersList.includes(trimmedEmail)) {
      setSpeakersList((prev) => [...prev, trimmedEmail]);
      if (isInvite) {
        setSpeakerInviteEmails((prev) => [...prev, trimmedEmail]);
      }
      setSpeakersEmailInput("");
      setShowSpeakerSuggestions(false);
      setSpeakerUserExists(null);
      toast.success(isInvite ? "Email invite added" : "Speaker added");
    } else {
      toast.warning("Email already added.");
    }
  };

  const handlePermalinkBlur = async () => {
    const permalink = vanityName.trim();
    if (!permalink || permalink.length < 2) return;
    // Skip check if unchanged from original
    if (permalink.toLowerCase() === originalPermalink.toLowerCase()) {
      setPermalinkExists(false);
      setPermalinkMessage("");
      return;
    }

    setPermalinkChecking(true);
    try {
      const response = await checkPermalinkExists(permalink, room?.id);
      const exists = response.isexist || false;
      setPermalinkExists(exists);
      setPermalinkMessage(exists ? "This URL is already taken" : "URL is available");
    } catch (error) {
      // Don't show error toast for API failures
    } finally {
      setPermalinkChecking(false);
    }
  };

  const handleSave = async () => {
    // Validation
    const newErrors = {};

    if (!meetingName.trim()) {
      newErrors.roomName = "Please enter a room name";
    } else if (meetingName.trim().length < 3) {
      newErrors.roomName = "Room name must be at least 3 characters";
    }

    if (!vanityName.trim()) {
      newErrors.roomUrl = "Please enter a Room URL";
    } else if (vanityName.trim().length < 2) {
      newErrors.roomUrl = "Room URL must be at least 2 characters";
    } else if (permalinkExists) {
      newErrors.roomUrl = "This URL is already taken";
    }

    if (roomSettings.requirePassword && !roomSettings.password.trim()) {
      newErrors.password = "Please enter a password";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error(Object.values(newErrors)[0]);
      return;
    }

    if (exitUrlEnabled && (!exitUrl.trim() || !isValidUrl(exitUrl.trim()))) {
      toast.error("Please enter a valid exit URL");
      return;
    }

    if (customButtonEnabled) {
      if (!customButtonText.trim() || customButtonText.trim().length < 3) {
        toast.error("Button name must be at least 3 characters long");
        return;
      }
      if (!customButtonUrl.trim() || !isValidUrl(customButtonUrl.trim())) {
        toast.error("Please enter a valid button URL");
        return;
      }
    }

    if (waitingRoomTextEnabled) {
      if (!waitingRoomTitle.trim() || waitingRoomTitle.trim().length < 3) {
        toast.error("Waiting room title must be at least 3 characters long");
        return;
      }
      if (!waitingRoomMessage.trim() || waitingRoomMessage.trim().length < 3) {
        toast.error("Waiting room message must be at least 3 characters long");
        return;
      }
      if (!waitingRoomSubText.trim() || waitingRoomSubText.trim().length < 3) {
        toast.error("Waiting room sub text must be at least 3 characters long");
        return;
      }
    }

    if (customBackgroundEnabled) {
      if (backgroundType === "image" && !backgroundImage && !backgroundImagePreview) {
        toast.error("Please select a background image");
        return;
      }
    }

    try {
      const formData = new FormData();
      formData.append("roomName", meetingName.trim());
      formData.append("roomPermalink", vanityName.trim());
      formData.append("description", description.trim());
      formData.append("roomType", "Meeting");

      Object.keys(roomSettings).forEach((key) => {
        const value = roomSettings[key];
        if (value !== null && value !== undefined && value !== "") {
          formData.append(key, value);
        }
      });

      if (invitesList.length > 0) {
        formData.append("emails", JSON.stringify(invitesList));
      }

      if (speakersList.length > 0) {
        formData.append("speakers", JSON.stringify(speakersList));
      }

      formData.append("exitUrlEnabled", exitUrlEnabled);
      formData.append("exitUrl", exitUrlEnabled && exitUrl.trim() ? exitUrl.trim() : "false");
      formData.append("customButtonEnabled", customButtonEnabled);
      if (customButtonEnabled) {
        formData.append("customButtonText", customButtonText.trim());
        formData.append("customButtonUrl", customButtonUrl.trim());
      }

      formData.append("waitingRoomTextEnabled", waitingRoomTextEnabled);
      if (waitingRoomTextEnabled) {
        formData.append("waitingRoomTitle", waitingRoomTitle.trim());
        formData.append("waitingRoomMessage", waitingRoomMessage.trim());
        formData.append("waitingRoomSubText", waitingRoomSubText.trim());
      }

      formData.append("customBackgroundEnabled", customBackgroundEnabled);
      if (customBackgroundEnabled) {
        formData.append("backgroundType", backgroundType);
        if (backgroundType === "solid") {
          formData.append("backgroundColor", backgroundColor);
        } else if (backgroundType === "gradient") {
          formData.append("gradientColor1", gradientColor1);
          formData.append("gradientColor2", gradientColor2);
          formData.append("gradientDirection", gradientDirection);
        } else if (backgroundType === "image" && backgroundImage) {
          formData.append("backgroundImage", backgroundImage);
        }
      }

      await updateMeetingRoom(room.id, formData);
      toast.success("Room updated successfully!");
      onUpdate();
      onClose();
    } catch (error) {
      console.error("Error updating room:", error);
      const serverMessage =
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message;
      toast.error(serverMessage || "Failed to update room");
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 99999 }}>
      <div className="bg-[#001233] rounded-2xl w-full max-w-full md:w-1/2 md:min-w-[800px] max-h-[90vh] flex flex-col border border-white/15 shadow-2xl shadow-black/50">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0">
          <h2 className="text-xl font-bold text-white">Edit Room</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Content — scrollable, takes remaining space between header and footer */}
        <div className="overflow-y-auto flex-1 min-h-0 p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-white text-sm">Loading...</div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Room Name and Vanity Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-white text-xs font-medium mb-1.5">
                    Room Name
                  </label>
                  <input
                    type="text"
                    value={meetingName}
                    onChange={(e) => { setMeetingName(e.target.value); if (errors.roomName) setErrors((prev) => ({ ...prev, roomName: "" })); }}
                    placeholder="My Podcast Studio"
                    className={`w-full px-3 py-2 text-sm bg-white/5 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#6a1fbf] ${errors.roomName ? "border-red-500" : "border-white/10"}`}
                  />
                  {errors.roomName && <p className="text-red-400 text-xs mt-1">{errors.roomName}</p>}
                </div>
                <div>
                  <label className="block text-white text-xs font-medium mb-1.5">
                    Room URL <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400 text-xs whitespace-nowrap">{user?.whitelabelDomain || window.location.host}/</span>
                    <input
                      type="text"
                      value={vanityName}
                      onChange={(e) => {
                        setVanityName(e.target.value.toLowerCase().replace(/[^a-z0-9\-_]/g, ''));
                        if (permalinkExists || permalinkMessage) {
                          setPermalinkExists(false);
                          setPermalinkMessage("");
                        }
                        if (errors.roomUrl) setErrors((prev) => ({ ...prev, roomUrl: "" }));
                      }}
                      onBlur={handlePermalinkBlur}
                      disabled={isBasicPlan}
                      placeholder="my-podcast"
                      maxLength={30}
                      className={`flex-1 px-3 py-2 text-sm bg-white/5 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#6a1fbf] ${isBasicPlan ? "opacity-50 cursor-not-allowed" : ""} ${errors.roomUrl ? "border-red-500" : "border-white/10"}`}
                    />
                  </div>
                  {!isBasicPlan && (
                    <p className="text-xs mt-1">
                      {errors.roomUrl ? (
                        <span className="text-red-400">{errors.roomUrl}</span>
                      ) : permalinkChecking ? (
                        <span className="text-blue-400">Checking availability...</span>
                      ) : permalinkMessage ? (
                        <span className={permalinkExists ? "text-red-400" : "text-green-400"}>{permalinkMessage}</span>
                      ) : (
                        <span className="text-gray-400">Letters, numbers, hyphens and underscores only</span>
                      )}
                    </p>
                  )}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-white text-xs font-medium mb-1.5">
                  Description (optional)
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Weekly podcast recordings"
                  className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#6a1fbf]"
                />
              </div>

              {/* Co-Host */}
              {user?.plan === "Business" && (
                <div>
                  <h3 className="text-white text-sm font-semibold mb-2">Co-Host</h3>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 relative">
                      <input
                        type="email"
                        value={invitesEmailInput}
                        onChange={(e) => setInvitesEmailInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && invitesEmailInput.trim() && !checkingUser) {
                            e.preventDefault();
                            handleAddCoHost(invitesEmailInput.trim(), userExists === false);
                          }
                        }}
                        placeholder="Add co-host email and press Enter"
                        className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#6a1fbf]"
                      />
                      {invitesEmailInput.trim() && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                          {checkingUser ? (
                            <div className="w-4 h-4 border-2 border-[#6a1fbf] border-t-transparent rounded-full animate-spin"></div>
                          ) : userExists === true ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : userExists === false ? (
                            <Mail className="w-4 h-4 text-orange-500" />
                          ) : null}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        if (invitesEmailInput.trim()) {
                          handleAddCoHost(invitesEmailInput.trim(), userExists === false);
                        }
                      }}
                      disabled={checkingUser}
                      className="px-3 py-2 text-sm bg-[#6a1fbf] text-white rounded-lg hover:bg-[#7b2fcf] transition-colors disabled:opacity-50"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {invitesList.map((email, index) => {
                      const isInvite = inviteEmails.includes(email);
                      return (
                        <span
                          key={index}
                          className={`flex items-center px-2 py-0.5 rounded-full text-xs ${
                            isInvite ? "bg-orange-600 text-white" : "bg-[#00c853]/20 text-[#00c853] border border-[#00c853]/30"
                          }`}
                        >
                          {isInvite && <Mail className="w-2.5 h-2.5 mr-1" />}
                          {email}
                          <button
                            onClick={() => {
                              setInvitesList((prev) => prev.filter((item) => item !== email));
                              setInviteEmails((prev) => prev.filter((item) => item !== email));
                            }}
                            className="ml-1.5 hover:text-red-400"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Speakers */}
              <div>
                <h3 className="text-white text-sm font-semibold mb-2">Speakers</h3>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 relative">
                    <input
                      type="email"
                      value={speakersEmailInput}
                      onChange={(e) => setSpeakersEmailInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && speakersEmailInput.trim() && !checkingSpeakerUser) {
                          e.preventDefault();
                          handleAddSpeaker(speakersEmailInput.trim(), speakerUserExists === false);
                        }
                      }}
                      placeholder="Add speaker email and press Enter"
                      className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#6a1fbf]"
                    />
                    {speakersEmailInput.trim() && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2">
                        {checkingSpeakerUser ? (
                          <div className="w-4 h-4 border-2 border-[#6a1fbf] border-t-transparent rounded-full animate-spin"></div>
                        ) : speakerUserExists === true ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : speakerUserExists === false ? (
                          <Mail className="w-4 h-4 text-orange-500" />
                        ) : null}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      if (speakersEmailInput.trim()) {
                        handleAddSpeaker(speakersEmailInput.trim(), speakerUserExists === false);
                      }
                    }}
                    disabled={checkingSpeakerUser}
                    className="px-3 py-2 text-sm bg-[#6a1fbf] text-white rounded-lg hover:bg-[#7b2fcf] transition-colors disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {speakersList.map((email, index) => {
                    const isInvite = speakerInviteEmails.includes(email);
                    return (
                      <span
                        key={index}
                        className={`flex items-center px-2 py-0.5 rounded-full text-xs ${
                          isInvite ? "bg-orange-600 text-white" : "bg-[#00c853]/20 text-[#00c853] border border-[#00c853]/30"
                        }`}
                      >
                        {isInvite && <Mail className="w-2.5 h-2.5 mr-1" />}
                        {email}
                        <button
                          onClick={() => {
                            setSpeakersList((prev) => prev.filter((item) => item !== email));
                            setSpeakerInviteEmails((prev) => prev.filter((item) => item !== email));
                          }}
                          className="ml-1.5 hover:text-red-400"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Room Settings */}
              <div>
                <h3 className="text-white text-sm font-semibold mb-3">Room Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { label: "Allow Chat", tip: "Enable or disable in-meeting chat for participants.", checked: roomSettings.allowChat, onChange: (v) => handleSettingChange("allowChat", v) },
                    { label: "Allow Screen Sharing", tip: "Let participants share their screen during the meeting.", checked: roomSettings.allowScreenSharing, onChange: (v) => handleSettingChange("allowScreenSharing", v) },
                    { label: "Mute Participants on Entry", tip: "Hosts can unmute participants later.", checked: roomSettings.muteParticipantsOnEntry, onChange: (v) => handleSettingChange("muteParticipantsOnEntry", v) },
                    { label: "Allow Reactions", tip: "Participants can use emoji reactions during the meeting.", checked: roomSettings.allowReactions, onChange: (v) => handleSettingChange("allowReactions", v) },
                    { label: "Allow Raise Hand", tip: "Participants can raise their hand to request attention from the host.", checked: roomSettings.allowRaiseHand, onChange: (v) => handleSettingChange("allowRaiseHand", v) },
                    { label: "Require Password", tip: "Participants will need to enter a password to join the meeting.", checked: roomSettings.requirePassword, onChange: (v) => handleSettingChange("requirePassword", v) },
                    { label: "Waiting Room", tip: "Participants will be placed in a waiting room before joining the meeting.", checked: roomSettings.waitingRoom, onChange: (v) => handleSettingChange("waitingRoom", v) },
                    { label: "Privacy Mode", tip: "Privacy mode is enabled. Recording, screenshots, and screen capture are disabled to protect this meeting.", checked: roomSettings.privacy, onChange: (v) => handleSettingChange("privacy", v) },
                    { label: "Exit URL", tip: "Redirect participants to a specific URL when they leave the meeting.", checked: exitUrlEnabled, onChange: (v) => { setExitUrlEnabled(v); if (v) setActiveSubModal("exitUrl"); if (!v) setExitUrl(""); }, subModal: "exitUrl", premium: true },
                    { label: "Custom Button", tip: "Add a custom button with a link in the meeting room.", checked: customButtonEnabled, onChange: (v) => { setCustomButtonEnabled(v); if (v) setActiveSubModal("customButton"); if (!v) { setCustomButtonText(""); setCustomButtonUrl(""); } }, subModal: "customButton", premium: true },
                    { label: "Waiting Room Text", tip: "Customize the messages shown to participants in the waiting room.", checked: waitingRoomTextEnabled, onChange: (v) => { setWaitingRoomTextEnabled(v); if (v) setActiveSubModal("waitingRoomText"); if (!v) { setWaitingRoomTitle(""); setWaitingRoomMessage(""); setWaitingRoomSubText(""); } }, subModal: "waitingRoomText", premium: true },
                    { label: "Custom Background", tip: "Add a custom background to your meeting room.", checked: customBackgroundEnabled, onChange: (v) => { setCustomBackgroundEnabled(v); if (v) setActiveSubModal("customBackground"); if (!v) { setBackgroundImage(null); setBackgroundImagePreview(null); if (fileInputRef.current) fileInputRef.current.value = ""; } }, subModal: "customBackground", premium: true },
                  ].map((item) => {
                    const isDisabled = item.premium && isBasicPlan;
                    return (
                    <div key={item.label} className={`flex items-center justify-between p-2.5 bg-white/5 border border-white/5 rounded-lg group relative ${isDisabled ? "opacity-50 pointer-events-none" : ""}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-white text-xs font-medium cursor-default">{item.label}</span>
                        {!isDisabled && item.subModal && item.checked && (
                          <button onClick={() => setActiveSubModal(item.subModal)} className="text-[10px] text-[#6a1fbf] hover:text-[#8b4fcf] underline">Edit</button>
                        )}
                      </div>
                      <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-50 pointer-events-none">
                        <div className="bg-gray-900 border border-white/20 text-gray-300 text-xs px-2.5 py-1.5 rounded-lg shadow-lg max-w-[260px]">{item.tip}</div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                        <input type="checkbox" checked={item.checked} onChange={(e) => item.onChange(e.target.checked)} disabled={isDisabled} className="sr-only peer" />
                        <div className="w-9 h-5 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#6a1fbf]"></div>
                      </label>
                    </div>
                    );
                  })}
                </div>

                {/* Password Input */}
                {roomSettings.requirePassword && (
                  <div className="mt-3">
                    <label className="block text-white text-xs font-medium mb-1">Password</label>
                    <input
                      type="text"
                      value={roomSettings.password}
                      onChange={(e) => { handleSettingChange("password", e.target.value); if (errors.password) setErrors((prev) => ({ ...prev, password: "" })); }}
                      placeholder="Enter password"
                      className={`w-full px-3 py-2 text-sm bg-white/5 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#6a1fbf] ${errors.password ? "border-red-500" : "border-white/10"}`}
                    />
                    {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer — shrink-0 so it's never clipped by the flex parent */}
        <div className="flex items-center justify-between p-4 border-t border-white/10 shrink-0">
          <div>
            {onDelete && (
              <button
                onClick={onDelete}
                className="px-4 py-2 text-sm border border-red-500/50 text-red-400 rounded-lg hover:bg-red-500/10 hover:text-red-300 transition-colors flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                <span>Delete Room</span>
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm bg-white/10 text-white rounded-lg hover:bg-white/15 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg hover:shadow-lg hover:shadow-purple-500/25 transition-all flex items-center gap-2"
            >
              <Check className="h-4 w-4" />
              <span>Save Changes</span>
            </button>
          </div>
        </div>
      </div>

      {/* Exit URL Sub-Modal */}
      {activeSubModal === "exitUrl" && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4" style={{ zIndex: 100000 }}>
          <div className="bg-[#2a3955] rounded-2xl w-full max-w-lg border border-white/10 shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-white text-base font-semibold">Exit URL</h3>
                <button onClick={() => setActiveSubModal(null)} className="p-1 hover:bg-white/10 rounded-lg transition-colors"><X className="h-4 w-4 text-gray-400" /></button>
              </div>
              <p className="text-gray-400 text-xs mb-5">Redirect participants to a specific URL when they leave the meeting.</p>
              <div>
                <label className="block text-white text-sm font-medium mb-2">Exit URL</label>
                <input type="url" value={typeof exitUrl === "string" ? exitUrl : ""} onChange={(e) => setExitUrl(e.target.value)} placeholder="https://example.com"
                  className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${exitUrl && typeof exitUrl === "string" && exitUrl.trim() && !isValidUrl(exitUrl.trim()) ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-[#6a1fbf]"}`} />
                {exitUrl && typeof exitUrl === "string" && exitUrl.trim() && !isValidUrl(exitUrl.trim()) && <p className="text-red-400 text-xs mt-1">Please enter a valid URL (e.g., https://example.com)</p>}
                {exitUrl && typeof exitUrl === "string" && exitUrl.trim() && isValidUrl(exitUrl.trim()) && <p className="text-green-400 text-xs mt-1">Valid URL</p>}
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-white/10">
              <button onClick={() => setActiveSubModal(null)} className="px-4 py-2 text-sm bg-white/10 text-white rounded-lg hover:bg-white/15 transition-colors">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Button Sub-Modal */}
      {activeSubModal === "customButton" && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4" style={{ zIndex: 100000 }}>
          <div className="bg-[#2a3955] rounded-2xl w-full max-w-lg border border-white/10 shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-white text-base font-semibold">Custom Button</h3>
                <button onClick={() => setActiveSubModal(null)} className="p-1 hover:bg-white/10 rounded-lg transition-colors"><X className="h-4 w-4 text-gray-400" /></button>
              </div>
              <p className="text-gray-400 text-xs mb-5">Add a custom button with a link in the meeting room.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Button Name</label>
                  <input type="text" value={customButtonText} onChange={(e) => setCustomButtonText(e.target.value)} placeholder="e.g., Visit Our Website" maxLength={50}
                    className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${customButtonText.trim() && customButtonText.trim().length < 3 ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-[#6a1fbf]"}`} />
                  <p className="text-gray-400 text-xs mt-1">{customButtonText.trim() && customButtonText.trim().length < 3 ? <span className="text-red-400">Minimum 3 characters required</span> : `${customButtonText.length}/50 characters`}</p>
                </div>
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Button URL</label>
                  <input type="url" value={customButtonUrl} onChange={(e) => setCustomButtonUrl(e.target.value)} placeholder="https://example.com"
                    className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${customButtonUrl.trim() && !isValidUrl(customButtonUrl.trim()) ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-[#6a1fbf]"}`} />
                  {customButtonUrl.trim() && !isValidUrl(customButtonUrl.trim()) && <p className="text-red-400 text-xs mt-1">Please enter a valid URL (e.g., https://example.com)</p>}
                  {customButtonUrl.trim() && isValidUrl(customButtonUrl.trim()) && <p className="text-green-400 text-xs mt-1">Valid URL</p>}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-white/10">
              <button onClick={() => setActiveSubModal(null)} className="px-4 py-2 text-sm bg-white/10 text-white rounded-lg hover:bg-white/15 transition-colors">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Waiting Room Text Sub-Modal */}
      {activeSubModal === "waitingRoomText" && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4" style={{ zIndex: 100000 }}>
          <div className="bg-[#2a3955] rounded-2xl w-full max-w-lg border border-white/10 shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-white text-base font-semibold">Waiting Room Text</h3>
                <button onClick={() => setActiveSubModal(null)} className="p-1 hover:bg-white/10 rounded-lg transition-colors"><X className="h-4 w-4 text-gray-400" /></button>
              </div>
              <p className="text-gray-400 text-xs mb-5">Customize the messages shown to participants in the waiting room.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Title <span className="text-red-500">*</span></label>
                  <input type="text" value={waitingRoomTitle} onChange={(e) => setWaitingRoomTitle(e.target.value)} placeholder="e.g., Waiting for Host to Approve" maxLength={100}
                    className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${waitingRoomTitle.trim() && waitingRoomTitle.trim().length < 3 ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-[#6a1fbf]"}`} />
                  <p className="text-gray-400 text-xs mt-1">{waitingRoomTitle.trim() && waitingRoomTitle.trim().length < 3 ? <span className="text-red-400">Minimum 3 characters required</span> : `${waitingRoomTitle.length}/100 characters`}</p>
                </div>
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Message <span className="text-red-500">*</span></label>
                  <textarea value={waitingRoomMessage} onChange={(e) => setWaitingRoomMessage(e.target.value)} placeholder="e.g., Please wait while the host reviews your request to join" maxLength={200} rows={3}
                    className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 resize-none ${waitingRoomMessage.trim() && waitingRoomMessage.trim().length < 3 ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-[#6a1fbf]"}`} />
                  <p className="text-gray-400 text-xs mt-1">{waitingRoomMessage.trim() && waitingRoomMessage.trim().length < 3 ? <span className="text-red-400">Minimum 3 characters required</span> : `${waitingRoomMessage.length}/200 characters`}</p>
                </div>
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Sub Text <span className="text-red-500">*</span></label>
                  <input type="text" value={waitingRoomSubText} onChange={(e) => setWaitingRoomSubText(e.target.value)} placeholder="e.g., You will be automatically admitted once approved" maxLength={150}
                    className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${waitingRoomSubText.trim() && waitingRoomSubText.trim().length < 3 ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-[#6a1fbf]"}`} />
                  <p className="text-gray-400 text-xs mt-1">{waitingRoomSubText.trim() && waitingRoomSubText.trim().length < 3 ? <span className="text-red-400">Minimum 3 characters required</span> : `${waitingRoomSubText.length}/150 characters`}</p>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-white/10">
              <button onClick={() => setActiveSubModal(null)} className="px-4 py-2 text-sm bg-white/10 text-white rounded-lg hover:bg-white/15 transition-colors">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Background Sub-Modal */}
      {activeSubModal === "customBackground" && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4" style={{ zIndex: 100000 }}>
          <div className="bg-[#2a3955] rounded-2xl w-full max-w-2xl border border-white/10 shadow-2xl max-h-[85vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-white text-base font-semibold">Custom Background</h3>
                <button onClick={() => setActiveSubModal(null)} className="p-1 hover:bg-white/10 rounded-lg transition-colors"><X className="h-4 w-4 text-gray-400" /></button>
              </div>
              <p className="text-gray-400 text-xs mb-5">Add a custom background to your meeting room.</p>

              {/* Type Tabs */}
              <div className="flex items-center gap-3 mb-6">
                {[
                  { key: "solid", label: "Solid Color" },
                  { key: "gradient", label: "Gradient" },
                  { key: "image", label: "Image" },
                ].map((tab) => (
                  <button key={tab.key} onClick={() => setBackgroundType(tab.key)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${backgroundType === tab.key ? "bg-[#6a1fbf] text-white" : "bg-gray-700 text-gray-400 hover:bg-gray-600"}`}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Solid Color */}
              {backgroundType === "solid" && (
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Background Color</label>
                  <div className="flex items-center gap-4">
                    <input type="color" value={backgroundColor} onChange={(e) => setBackgroundColor(e.target.value)} className="w-16 h-10 rounded border border-gray-600 cursor-pointer" />
                    <input type="text" value={backgroundColor} onChange={(e) => setBackgroundColor(e.target.value)} placeholder="#1a1a2e"
                      className="px-4 py-3 bg-white border border-gray-300 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6a1fbf] w-40" />
                    <div className="w-20 h-10 rounded border border-gray-600" style={{ backgroundColor }} />
                  </div>
                </div>
              )}

              {/* Gradient */}
              {backgroundType === "gradient" && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">First Color</label>
                    <div className="flex items-center gap-4">
                      <input type="color" value={gradientColor1} onChange={(e) => setGradientColor1(e.target.value)} className="w-16 h-10 rounded border border-gray-600 cursor-pointer" />
                      <input type="text" value={gradientColor1} onChange={(e) => setGradientColor1(e.target.value)} placeholder="#1a1a2e"
                        className="px-4 py-3 bg-white border border-gray-300 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6a1fbf] w-40" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Second Color</label>
                    <div className="flex items-center gap-4">
                      <input type="color" value={gradientColor2} onChange={(e) => setGradientColor2(e.target.value)} className="w-16 h-10 rounded border border-gray-600 cursor-pointer" />
                      <input type="text" value={gradientColor2} onChange={(e) => setGradientColor2(e.target.value)} placeholder="#615fff"
                        className="px-4 py-3 bg-white border border-gray-300 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6a1fbf] w-40" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Gradient Direction</label>
                    <select value={gradientDirection} onChange={(e) => setGradientDirection(e.target.value)}
                      className="px-4 py-3 bg-white border border-gray-300 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-[#6a1fbf]">
                      <option value="to right">Left to Right</option>
                      <option value="to left">Right to Left</option>
                      <option value="to bottom">Top to Bottom</option>
                      <option value="to top">Bottom to Top</option>
                      <option value="to bottom right">Diagonal ↘</option>
                      <option value="to bottom left">Diagonal ↙</option>
                      <option value="to top right">Diagonal ↗</option>
                      <option value="to top left">Diagonal ↖</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Preview</label>
                    <div className="w-full h-20 rounded-lg border border-gray-600" style={{ background: `linear-gradient(${gradientDirection}, ${gradientColor1}, ${gradientColor2})` }} />
                  </div>
                </div>
              )}

              {/* Image */}
              {backgroundType === "image" && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Background Image</label>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) { toast.error("Image size should not exceed 5MB"); e.target.value = ""; return; }
                        setBackgroundImage(file);
                        const reader = new FileReader();
                        reader.onloadend = () => setBackgroundImagePreview(reader.result);
                        reader.readAsDataURL(file);
                      }
                    }} className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#6a1fbf] file:text-white hover:file:bg-[#7b2fcf] file:cursor-pointer cursor-pointer" />
                    <p className="text-gray-400 text-xs mt-1">Maximum file size: 5MB</p>
                    {backgroundImage && <p className="text-green-400 text-xs mt-1">Selected: {backgroundImage.name} ({(backgroundImage.size / 1024).toFixed(2)} KB)</p>}
                  </div>
                  {backgroundImagePreview && (
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">Preview</label>
                      <div className="relative w-full h-48 rounded-lg overflow-hidden border border-gray-600 bg-gray-800">
                        <img src={backgroundImagePreview} alt="Background preview" className="w-full h-full object-contain" />
                        <button onClick={() => { setBackgroundImage(null); setBackgroundImagePreview(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                          className="absolute top-2 right-2 p-2 bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1">
                          <X size={14} /><span className="text-xs">Remove</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-white/10">
              <button onClick={() => setActiveSubModal(null)} className="px-4 py-2 text-sm bg-white/10 text-white rounded-lg hover:bg-white/15 transition-colors">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default EditRoomModal;
